import { Measure } from '@cbsi-cbscom/measure';
import {
	AudioTrackInterface, QualityCategory, ResourceConfigurationInterface, TextTrackInterface, VideoPlayerInterface
} from '@cbsinteractive/avia-js';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { PlayerAdapterPlaybackState } from 'library/ts/player/adapter/enums/PlayerAdapterPlaybackState';
import { PlayerAdapterStreamType } from 'library/ts/player/adapter/enums/PlayerAdapterStreamType';
import { PlayerAdapterVideoQuality } from 'library/ts/player/adapter/enums/PlayerAdapterVideoQuality';
import { IAdCuePoint } from 'library/ts/player/adapter/interfaces/ads/IAdCuePoint';
import { IAudioTrack } from 'library/ts/player/adapter/interfaces/audio/IAudioTrack';
import { IVideoQuality } from 'library/ts/player/adapter/interfaces/quality/IVideoQuality';
import { IResourceConfig } from 'library/ts/player/adapter/interfaces/resource/config/IResourceConfig';
import { ITextTrack } from 'library/ts/player/adapter/interfaces/text/ITextTrack';
import { IThumbnailData } from 'library/ts/player/adapter/interfaces/thumbnail/IThumbnailData';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagOnceSettingsProperties } from 'tag/ts/tag/model/enums/TagOnceSettingsProperties';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { AviaPlayerWrapper } from 'tag/ts/tag/player/adapters/avia/AviaPlayerWrapper';
import { IAviaAPITransformerArgument } from 'tag/ts/tag/player/adapters/avia/interfaces/arguments/IAviaAPITransformerArgument';
import { IAviaAPI } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaAPI';
import { AviaEventTransformer } from 'tag/ts/tag/player/adapters/avia/transformers/AviaEventTransformer';
import 'tag/ts/tag/player/adapters/avia/utilities/AviaErrorHandler';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description VTG Avia player API transformer for Smart Tag integration
 *
 * **API Translation Layer:**
 * Translates VTG Avia-js player APIs into Smart Tag's standardized player interface.
 * Acts as the adapter layer that allows Smart Tag components to interact with VTG
 * player functionality through a consistent API, regardless of VTG implementation changes.
 *
 * **Core Responsibilities:**
 * - **API Standardization**: Converts VTG method signatures to Smart Tag conventions
 * - **Data Type Transformation**: Maps VTG data structures to Smart Tag interfaces
 * - **Error Handling**: Wraps VTG operations with Smart Tag error management
 * - **State Management**: Maintains player state accessible to Smart Tag components
 * - **Event Integration**: Coordinates with AviaEventTransformer for event handling
 *
 * **API Categories:**
 * - **Playback Control**: play(), pause(), stop(), seek(), goLive()
 * - **Resource Management**: loadResource() for content loading
 * - **Quality Control**: videoQuality, audioTracks, textTracks management
 * - **State Access**: currentTime, duration, playbackState, isPlayingLive
 * - **Plugin Integration**: getPlugin() for VTG plugin access
 * - **Event Lifecycle**: addPlayerEventListener(), removePlayerEventListener()
 */
export class AviaAPITransformer extends AbstractTagClass implements IAviaAPI {

	// reference to AviaPlayer class instance
	#aviaPlayerWrapper: AviaPlayerWrapper;
	// reference to AviaEventTransformer class instance
	#aviaEventTransformer: AviaEventTransformer;
	// flag indicating active VTG player events setup
	#playerEventsActive: boolean = false;
	// cleanup function for video controls (right-click suppression, etc.)
	#videoControlsCleanup: ( () => void ) | null = null;

	constructor( argument: IAviaAPITransformerArgument ) {
		super(
			{
				...argument,
				name: 'AviaAPITransformer'
			}
		);
		// store instance of AviaPlayer class for later use
		this.#aviaPlayerWrapper = argument?.aviaPlayerWrapper;
		// store instance of AviaEventTransformer class for later use
		this.#aviaEventTransformer = argument?.aviaEventTransformer;
	}

	/**
	 * @description Merge utility method (uses optional chaining to guard against race conditions during cleanup)
	 * @param sourceObject
	 * @returns {unknown}
	 */
	#getMerged = ( sourceObject: unknown ): unknown => {
		if ( !this.utils?.Type?.isEmpty( sourceObject ) && this.utils?.Type?.isObject( sourceObject ) ) {
			return this.utils?.DeepMerge?.deepmerge( {}, sourceObject ) as unknown;
		}
		return undefined;
	};

	/**
	 * @description Returns instance of adapted player API
	 * @returns {VideoPlayerInterface}
	 */
	get adaptedPlayer(): VideoPlayerInterface {
		return this.#aviaPlayerWrapper?.adaptedPlayerInstance;
	}

	/**
	 * @description Returns avia player id
	 * @returns {string}
	 */
	get id() {
		return this?.adaptedPlayer?.id;
	}

	/**
	 * @description Adds VTG player event listeners
	 * Note: Double null-checking is intentional to prevent race conditions during rapid tag operations
	 */
	addPlayerEventListener = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				// Guard: player must exist and not be empty (optional chaining handles utils unavailability)
				if ( !this.adaptedPlayer || this.utils?.Type?.isEmpty( this?.adaptedPlayer ) !== false ) {
					reject( { msg: 'Avia player instance is undefined or unavailable' } );
					return;
				}

				// attempt removing events first
				this.removePlayerEventListener().then(
					() => {
						// Double-check player still exists after async operation (optional chaining handles utils unavailability)
						if ( !this.adaptedPlayer || this.utils?.Type?.isEmpty( this?.adaptedPlayer ) !== false ) {
							reject( { msg: 'Avia player instance became undefined during setup' } );
							return;
						}

						if ( this.utils?.Type?.isObject( window?.avia?.PlayerEvent ) ) {
							this?.logger?.log( 'addPlayerEventListener:', 'subscribe to avia events...' );
							try {
								Object.keys( window.avia.PlayerEvent ).forEach(
									( key ) => {
										// filter by avia event. we can use this switch to ignore avia events deprecated by VTG
										switch ( window.avia.PlayerEvent[ key ] ) {
											default: // and we are subscribing to all other avia events
												this.adaptedPlayer.on( window.avia.PlayerEvent[ key ], this.#aviaEventTransformer.onPlayerEvent );
										}
									}
								);
							} catch ( error ) {
								this?.logger?.warn( 'Error during event listener registration:', error );
								reject( { msg: 'Event listener registration failed', error } );
								return;
							}
						}
						this.#playerEventsActive = true; // we are listening to VTG player events
						resolve( { msg: 'Avia player event listener setup done.' } );
					}
				).catch( ( error ) => {
					reject( { msg: 'Failed to remove existing event listeners', error } );
				} );
			}
		);
	};

	/**
	 * @description Removes VTG player event listeners
	 * @return {Promise<{ msg: string }>}
	 */
	removePlayerEventListener = (): Promise<{ msg: string }> => {
		return new Promise(
			( resolve ) => {
				// Optional chaining handles utils unavailability during cleanup
				if ( this.adaptedPlayer && this.utils?.Type?.isFunction( this?.adaptedPlayer?.off ) ) {
					if ( this.utils?.Type?.isObject( window?.avia?.PlayerEvent ) ) {
						this?.logger?.log( 'removePlayerEventListener:', 'unsubscribe from avia events...' );

						// Remove each event listener individually with error handling
						Object.keys( window.avia.PlayerEvent ).forEach( ( key ) => {
							try {
								this.adaptedPlayer.off( window.avia.PlayerEvent[ key ], this.#aviaEventTransformer.onPlayerEvent );
							} catch ( error ) {
								this?.logger?.warn( `Failed to remove event listener for ${key}:`, error );
							}
						} );
					}
				} else {
					this?.logger?.log( 'removePlayerEventListener:', 'avia player does not exist, off method unavailable, or utils unavailable' );
				}

				this.#playerEventsActive = false; // we are not listening to VTG player events

				// Clean up video control listeners (right-click suppression, etc.)
				if ( this.#videoControlsCleanup ) {
					try {
						this.#videoControlsCleanup();
						this.#videoControlsCleanup = null;
					} catch ( error ) {
						this?.logger?.warn( 'Failed to clean up video controls:', error );
					}
				}

				// Guard against race condition: use fallback if utils.Delay is unavailable
				if ( this.utils?.Delay?.delay ) {
					this.utils.Delay.delay().then(
						() => {
							resolve( { msg: 'Unsubscribed from avia player events.' } );
						}
					);
				} else {
					// Fallback: resolve immediately if Delay utility is unavailable (cleanup scenario)
					resolve( { msg: 'Unsubscribed from avia player events (utils unavailable).' } );
				}
			}
		);
	};

	/**
	 * @description External interface method - converts Player Adapter resource to avia-js and delegates
	 * @param {IResourceConfig} resource - Player Adapter resource
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	loadResource = ( resource: IResourceConfig ): Promise<IPromiseResolveGeneric> => {
		// 🔄 ARCHITECTURAL BOUNDARY: Player Adapter → avia-js
		// Converts external IResourceConfig to internal ResourceConfigurationInterface
		// This enables external consumers to use Player Adapter types while internal logic uses native avia-js types
		const aviaResource = resource as unknown as ResourceConfigurationInterface; // Safe: identical underlying structure
		return this.#loadAviaResource( aviaResource );
	};

	/**
	 * @description Internal method - loads avia-js resource config into avia player
	 * 🏗️ INTERNAL AVIA-JS LAYER: Works exclusively with native avia-js interfaces
	 * @param {ResourceConfigurationInterface} resource - Native avia-js resource
	 * @returns {Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }>}
	 */
	#loadAviaResource = ( resource: ResourceConfigurationInterface ): Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }> => {
		return new Promise(
			( resolve, reject ) => {
				// Initial validation
				if ( !this.adaptedPlayer || !this.utils.Type.isFunction( this.adaptedPlayer.attachResource ) ) {
					const errorMsg = 'Player or attachResource method not available';
					this?.logger?.warn( 'loadResource:', errorMsg );
					reject( { msg: errorMsg, error: new Error( errorMsg ) } );
					return;
				}

				// Last hop before avia: re-apply query-dot escaping defensively. Site-supplied
				// resource.location.mediaUrl overrides deep-merge in AFTER the builder's
				// setMediaUrl escaping and would otherwise reach Shaka unescaped, breaking its
				// manifest type detection (Shaka 4000 → avia 2103). Idempotent — no-op on URLs
				// already escaped by the builders. See utils.URL.escapeQueryDots.
				if ( this.utils.Type.isString( resource?.location?.mediaUrl ) ) {
					resource.location.mediaUrl = this.utils.URL.escapeQueryDots( resource.location.mediaUrl );
				}

				this?.logger?.log( 'loadResource:', '\nresource.location.mediaUrl:', resource?.location?.mediaUrl, '\nresource.ad.ssai:', resource?.ad?.ssai );
				Measure.end( Metrics.loadToAttachResourceTime );
				Measure.start( Metrics.attachToVideoStartTime );

				// Get the delay value
				const delayMs = this?.tag?.model?.onceSettingsManager?.getOnceSettings( TagOnceSettingsProperties.DELAY_ATTACH_RESOURCE_MS ) as number;

				// Helper function to execute attachResource
				const executeAttachResource = (): void => {
					// Final validation prevents race condition where player is destroyed during delay
					if ( !this.adaptedPlayer || !this.utils.Type.isFunction( this.adaptedPlayer.attachResource ) ) {
						const errorMsg = 'Player instance unavailable after delay';
						this?.logger?.warn( 'loadResource:', 'Player cleanup during operation - this is expected during rapid tag operations' );
						reject( { msg: errorMsg, error: new Error( errorMsg ) } );
						return;
					}

					this.adaptedPlayer.attachResource( resource ).then(
						( attachedResource ) => {
							this?.logger?.log( 'loadResource:', '\navia attachResource success:', attachedResource );

							// IMPORTANT: disable picture-in-picture etc. on the video element on desktop
							if ( this?.params?.SYSTEM?.embedType === EmbedType.DESKTOP ) {
								const videoElement = this?.tag?.view?.container?.getElementsByTagName( 'video' )[ 0 ];
								if ( videoElement ) {
									this.utils.DOM.disablePictureInPicture( videoElement );
									// Store the cleanup function for later removal
									const cleanup = this.utils.DOM.setupCustomVideoControls( videoElement );
									if ( cleanup ) {
										this.#videoControlsCleanup = cleanup;
									}
								} else {
									this?.logger?.warn( 'No video element found in container' );
								}
							}

							resolve( { msg: 'Resource attached', resource: attachedResource } );
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'loadResource:', '\navia attachResource ERROR:', error );
							reject( { msg: 'avia attachResource ERROR', error: error } );
						}
					);
				};

				// If no delay is specified or delay is 0, execute immediately
				if ( !delayMs || delayMs <= 0 ) {
					executeAttachResource();
					return;
				}

				// Use delay with additional safety checks
				this.utils.Delay.delay( delayMs ).then(
					() => {
						executeAttachResource();
					}
				).catch(
					( delayError ) => {
						this?.logger?.warn( 'loadResource:', 'Delay failed:', delayError );
						// Still attempt to attach resource even if delay failed
						executeAttachResource();
					}
				);
			}
		);
	};

	/**
	 * @description Play operation
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	play = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				if ( this.utils.Type.isFunction( this?.adaptedPlayer?.play ) ) {
					this.adaptedPlayer.play().then(
						() => {
							resolve( { msg: 'Play operation done' } );
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'player.play() failure:', error );
							reject( { msg: 'Play operation error', result: error } );
						}
					);
				} else {
					reject( { msg: 'Play operation error' } );
				}
			}
		);
	};

	/**
	 * @description Pause operation
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	pause = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				if ( this.utils.Type.isFunction( this?.adaptedPlayer?.pause ) ) {
					this.adaptedPlayer.pause().then(
						() => {
							resolve( { msg: 'Pause operation done' } );
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'player.pause() failure:', error );
							reject( { msg: 'Pause operation error', result: error } );
						}
					);
				} else {
					reject( { msg: 'Pause operation error' } );
				}
			}
		);
	};

	/**
	 * @description Stop operation
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	stop = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				// if player exists
				if ( this.utils.Type.isEmpty( this?.adaptedPlayer ) === false ) {
					this.adaptedPlayer.stop().then(
						() => {
							this?.logger?.log( 'player.stop() success' );
							resolve( { msg: 'Stop operation done' } );
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'player.stop() failure:', error );
							reject( { msg: 'Stop operation error', result: error } );
						}
					);
				} else {
					this?.logger?.warn( 'player.stop() failure:', 'player.stop does not exist' );
					reject( { msg: 'Stop operation error' } );
				}
			}
		);
	};

	/**
	 * @description Seek  operation
	 * @param {number} time
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	seek = ( time: number ): Promise<IPromiseResolveGeneric> => {
		this?.logger?.log( 'seek() called', 'time:', time );
		return new Promise(
			( resolve, reject ) => {
				// Enhanced parameter validation to prevent avia library errors
				if ( !this.utils.Type.isNumber( time ) ) {
					this?.logger?.warn( 'Seek operation failed: time parameter is not a number', 'time:', time, 'typeof time:', typeof time );
					reject( { msg: 'Seek operation. Invalid seek time - not a number', time } );
					return;
				}

				// Check for NaN, Infinity, and negative values
				if ( isNaN( time ) ) {
					this?.logger?.warn( 'Seek operation failed: time parameter is NaN:', time );
					reject( { msg: 'Seek operation. Invalid seek time - NaN detected', time } );
					return;
				}

				if ( !isFinite( time ) ) {
					this?.logger?.warn( 'Seek operation failed: time parameter is not finite:', time );
					reject( { msg: 'Seek operation. Invalid seek time - Infinity detected', time } );
					return;
				}

				if ( time < 0 ) {
					this?.logger?.warn( 'Seek operation failed: time parameter is negative:', time );
					reject( { msg: 'Seek operation. Invalid seek time - negative value', time } );
					return;
				}

				// if seek method exists
				if ( this.utils.Type.isFunction( this?.adaptedPlayer?.seek ) ) {
					this?.logger?.log( 'seek is a valid function on adaptedPlayer', 'time:', time );
					this.adaptedPlayer.seek( time ).then(
						() => {
							resolve( { msg: 'Seek operation done' } );
						}
					).catch(
						( error ) => {
							this?.logger?.warn( 'Seek operation failed in avia player:', error, 'time:', time );
							reject( { msg: 'Seek operation failed in underlying player', error, time } );
						}
					);
				} else {
					this?.logger?.warn( 'Seek operation failed: adaptedPlayer.seek method not available' );
					reject( { msg: 'Seek operation error - method not available' } );
				}
			}
		);
	};

	/**
	 * @description Go live operation
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	goLive = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				if ( this.utils.Type.isFunction( this?.adaptedPlayer?.goLive ) ) {
					this?.logger?.log( 'goLive' );
					this.adaptedPlayer.goLive();
					resolve( { msg: 'goLive operation done' } );
				} else {
					reject( { msg: 'goLive method does not exist on player instance' } );
				}
			}
		);
	};

	/**
	 * @description Returns a list of cue points with information about the thumbnail images available for that playhead time.
	 * @param {number} time
	 * @returns {Promise<IThumbnailData[]>}
	 */
	getThumbnails = ( time: number ): Promise<IThumbnailData[]> => {
		return new Promise(
			( resolve, reject ) => {
				if ( !this?.adaptedPlayer?.getThumbnails ) {
					reject( new Error( 'Player or getThumbnails method unavailable' ) );
					return;
				}

				try {
					const thumbnailsPromise = this.adaptedPlayer.getThumbnails( time );
					if ( !thumbnailsPromise || !thumbnailsPromise.then ) {
						reject( new Error( 'getThumbnails returned invalid promise' ) );
						return;
					}

					thumbnailsPromise.then(
						( data ) => {
							resolve( data as IThumbnailData[] );
						}
					).catch(
						( error ) => {
							reject( error );
						}
					);
				} catch ( error ) {
					reject( error );
				}
			}
		);
	};

	/**
	 * @description Force an ad to be skipped, if supported. Requires registering a Hook to enable functionality
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	skipAd = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				if ( this.utils.Type.isFunction( this?.adaptedPlayer?.skipAd ) ) {
					this?.logger?.log( 'skipAd' );
					try {
						this.adaptedPlayer.skipAd();
						this.utils.Delay.delay().then(
							() => {
								resolve( { msg: 'skipAd operation done' } );
							}
						);
					} catch ( error ) {
						this?.logger?.warn( 'skipAd ERROR:', error );
						reject( { msg: 'skipAd ERROR:', error } );
					}
				} else {
					reject( { msg: 'skipAd method does not exist on player instance' } );
				}
			}
		);
	};

	/**
	 * @description Retrieves a plugin by id. This Id is supplied by the plugin's getId() method.
	 * @param {string} id The Plugin's ID
	 * @returns {unknown} The plugin if available
	 */
	getPlugin = ( id: string ): unknown => {
		return this?.adaptedPlayer?.getPlugin?.( id );
	};

	/**
	 * @description The value returned here indicates whether the Player instance is ready to accept a resource.
	 * @returns {boolean}
	 */
	get isReady() {
		return true;
	}

	/**
	 * @description Returns player resource
	 * @returns {ResourceConfigurationInterface | undefined}
	 */
	get resource() {
		return this.#getMerged( this?.adaptedPlayer?.resource );
	}

	/**
	 * @description Indicates whether the current stream is playing from the live edge. Only applicable for streams with a streamType of LIVE or DVR.
	 */
	get isPlayingLive() {
		return this?.adaptedPlayer?.isPlayingLive;
	}

	/**
	 * @description Returns video quality info for current stream
	 * @returns {PlayerAdapterStreamType}
	 */
	get streamType(): PlayerAdapterStreamType {
		return this?.adaptedPlayer?.streamType as unknown as PlayerAdapterStreamType;
	}

	/**
	 * @description Returns an array of the available video bitrates
	 */
	get qualities() {
		return this.#getMerged( this?.adaptedPlayer?.qualities ) as IVideoQuality[];
	}

	/**
	 * @description Gets current video quality
	 */
	get quality() {
		return this.#getMerged( this?.adaptedPlayer?.quality ) as IVideoQuality;
	}

	/**
	 * @description Gets / sets current video quality category
	 */
	get videoQuality() {
		return this?.adaptedPlayer?.qualityCategory as unknown as PlayerAdapterVideoQuality;
	}

	set videoQuality( videoQuality: PlayerAdapterVideoQuality ) {
		if ( !this.utils.Type.isEmpty( this.adaptedPlayer ) ) {
			this.adaptedPlayer.qualityCategory = videoQuality as unknown as QualityCategory;
		}
	}

	/**
	 * @description Gets / sets auto bitrate switching
	 */
	get autoQualitySwitching() {
		return this?.adaptedPlayer?.autoQualitySwitching;
	}

	set autoQualitySwitching( auto: boolean ) {
		if ( !this.utils.Type.isEmpty( this.adaptedPlayer ) ) {
			this.adaptedPlayer.autoQualitySwitching = auto;
		}
	}

	/**
	 * @description Returns minimum bitrate from avia player
	 * @returns {number}
	 */
	get minBitrate() {
		return this?.adaptedPlayer?.minBitrate;
	}

	/**
	 * @description Sets minimum bitrate on avia player
	 * @param {number} bitrate
	 */
	set minBitrate( bitrate ) {
		if ( !this.utils.Type.isEmpty( this.adaptedPlayer ) ) {
			this.adaptedPlayer.minBitrate = bitrate;
		}
	}

	/**
	 * @description Returns maximum bitrate from avia player
	 * @returns {number}
	 */
	get maxBitrate() {
		return this?.adaptedPlayer?.maxBitrate;
	}

	/**
	 * @description Sets maximum bitrate on avia player
	 * @param {number} bitrate
	 */
	set maxBitrate( bitrate ) {
		if ( !this.utils.Type.isEmpty( this.adaptedPlayer ) ) {
			this.adaptedPlayer.maxBitrate = bitrate;
		}
	}

	/**
	 * @description Returns an array of the available audio tracks
	 * @returns {IAudioTrack[]}
	 */
	get audioTracks() {
		return this.#getMerged( this?.adaptedPlayer?.audioTracks ) as IAudioTrack[];
	}

	/**
	 * @description Returns current audio track from avia player
	 * @returns {IAudioTrack}
	 */
	get audioTrack() {
		return this.#getMerged( this?.adaptedPlayer?.audioTrack ) as IAudioTrack;
	}

	/**
	 * @description Sets audio track on avia player
	 * @param {IAudioTrack} track
	 */
	set audioTrack( track: IAudioTrack ) {
		if ( !this.utils.Type.isEmpty( this.adaptedPlayer ) ) {
			this.adaptedPlayer.audioTrack = track as unknown as AudioTrackInterface;
		}
	}

	/**
	 * @description Returns an array of the available text tracks
	 * @returns {ITextTrack[]}
	 */
	get textTracks() {
		return this.#getMerged( this?.adaptedPlayer?.textTracks ) as ITextTrack[];
	}

	/**
	 * @description Returns current text track from avia player
	 * @returns {ITextTrack}
	 */
	get textTrack() {
		return this.#getMerged( this?.adaptedPlayer?.textTrack ) as ITextTrack;
	}

	/**
	 * @description Sets text track on avia player
	 * @param {ITextTrack} track
	 */
	set textTrack( track: ITextTrack ) {
		if ( !this.utils.Type.isEmpty( this.adaptedPlayer ) ) {
			this.adaptedPlayer.textTrack = track as unknown as TextTrackInterface;
		}
	}

	/**
	 * @description Returns text track enabled / disabled status from avia player
	 * @returns {boolean}
	 */
	get textTrackEnabled() {
		return this?.adaptedPlayer?.textTrackEnabled;
	}

	/**
	 * @description Enables / disables text track on avia player
	 * @param {boolean} enabled
	 */
	set textTrackEnabled( enabled: boolean ) {
		if ( !this.utils.Type.isEmpty( this.adaptedPlayer ) ) {
			this.adaptedPlayer.textTrackEnabled = enabled;
		}
	}

	/**
	 * @description Returns content playback time
	 * @returns {number | undefined}
	 */
	get contentTime() {
		return this?.adaptedPlayer?.contentTime;
	}

	/**
	 * @description Returns content duration
	 * @returns {number | undefined}
	 */
	get contentDuration() {
		return this?.adaptedPlayer?.contentDuration;
	}

	/**
	 * @description Returns stream playback time
	 * @returns {number | undefined}
	 */
	get streamTime() {
		return this?.adaptedPlayer?.streamTime;
	}

	/**
	 * @description Returns stream duration
	 * @returns {number | undefined}
	 */
	get streamDuration() {
		return this?.adaptedPlayer?.streamDuration;
	}

	/**
	 * @description Returns the UTC timecode for a live stream's current playhead time. Only applicable for streams with a streamType of LIVE or LTS.
	 * @returns {number | undefined}
	 */
	get liveStreamUtcTime() {
		return this?.adaptedPlayer?.liveStreamUtcTime;
	}

	/**
	 * @description Returns the UTC timecode for a live stream's current duration. Only applicable for streams with a streamType of LIVE or LTS
	 * @returns {number | undefined}
	 */
	get liveStreamUtcDuration() {
		return this?.adaptedPlayer?.liveStreamUtcDuration;
	}

	/**
	 * @description Returns the UTC timecode for the stream of a live stream. Only applicable for streams with a streamType of LIVE or LTS.
	 * @returns {number | undefined}
	 */
	get liveStreamUtcStart() {
		return this?.adaptedPlayer?.liveStreamUtcStart;
	}

	/**
	 * @description Returns the playback time elapsed, in seconds, within a single resource playback session. Time is only measured when the playhead is advancing.
	 * @returns {number | undefined}
	 */
	get playbackTime() {
		return this?.adaptedPlayer?.playbackTime;
	}

	/**
	 * @description Returns current player state
	 * @returns {PlayerAdapterPlaybackState}
	 */
	get playbackState(): PlayerAdapterPlaybackState {
		return this?.adaptedPlayer?.playbackState as unknown as PlayerAdapterPlaybackState;
	}

	/**
	 * @description Returns avia player sound volume
	 * @returns {number | undefined}
	 */
	get volume() {
		return this?.adaptedPlayer?.volume;
	}

	/**
	 * @description Sets sound volume on avia player instance
	 * @param {number} value
	 */
	set volume( value: number ) {
		if ( !this.utils.Type.isEmpty( this.adaptedPlayer ) ) {
			this.adaptedPlayer.volume = value;
		}
	}

	/**
	 * @description Returns avia player sound mute status
	 * @returns {boolean | undefined}
	 */
	get muted() {
		return this?.adaptedPlayer?.muted;
	}

	/**
	 * @description Sets sound mute on avia player instance
	 * @param {boolean} value
	 */
	set muted( value: boolean ) {
		if ( !this.utils.Type.isEmpty( this.adaptedPlayer ) ) {
			this.adaptedPlayer.muted = value;
		}
	}

	/**
	 * @description The value returned here indicates whether the Player currently playing an advertisement
	 * @returns {boolean | undefined}
	 */
	get isAd() {
		return this?.adaptedPlayer?.isAd;
	}

	/**
	 * @description Returns ad cue points
	 * @returns {IAdCuePoint[]}
	 */
	get adBreakTimes() {
		return this.#getMerged( this?.adaptedPlayer?.getAdBreakTimes() ) as IAdCuePoint[];
	}

	/**
	 * @description Returns the JSON string detailing the player's configuration. Supplied mainly for Player diagnostics and testing.
	 * @returns {(spacing?: number) => string}
	 */
	get getConfigAsJson(): ( spacing?: number ) => string {
		return this?.adaptedPlayer?.getConfigAsJson;
	}

	/**
	 * @description Registers a hook with the Player. Hooks can be registered at any point in the player's life cycle.
	 * @see {@link https://avia.vtg.paramount.tech/avia-js/latest/docs/apis/avia-js/interfaces/VideoPlayerInterface#registerhook}
	 */
	get registerHook() {
		if ( !this?.adaptedPlayer ) {
			this?.logger?.warn( 'registerHook: Player instance unavailable' );
			return () => {
			}; // Return no-op function
		}
		return this?.adaptedPlayer?.registerHook;
	}

	/**
	 * @description Remove a specific hook for a given type.
	 * @see {@link https://avia.vtg.paramount.tech/avia-js/latest/docs/apis/avia-js/interfaces/VideoPlayerInterface#removehook}
	 */
	get removeHook() {
		if ( !this?.adaptedPlayer ) {
			this?.logger?.warn( 'removeHook: Player instance unavailable' );
			return () => {
			}; // Return no-op function
		}
		return this?.adaptedPlayer?.removeHook;
	}

}